import { useMemo, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import {
  FunnelConfigTypes,
  FunnelContextArea,
  FunnelOnSuccessResult,
  EventContextArea,
} from "../../../components/funnel/types.ts";
import { useGetNavigationContext } from "../../../hooks/useGetNavigationContext.ts";
import { usePaywallType } from "../../../hooks/usePaywallType.ts";
import { useUserData } from "../../../hooks/useUserData.ts";
import { Release } from "../../../types.ts";
import { useReleaseData } from "./hookstate/useReleaseData.ts";
import { FUNNEL_TYPES } from "../../../components/funnel/utils/contants.ts";
import { setFunnelModalConfig } from "../../../components/funnel/FunnelModal.tsx";
import { doesUserHaveAccess } from "../../../utils/doesUserHaveAccess.ts";
import { useAccountData } from "../../../hooks/useAccountData.ts";

export function useReaderPaywall({
  showPaywall,
  openType,
  setFunnelConfig = setFunnelModalConfig,
  onClose,
  modalContext = FunnelContextArea.reader,
  setFunnelPending,
}: {
  showPaywall: boolean;
  openType?: FUNNEL_TYPES;
  setFunnelConfig?: (config?: FunnelConfigTypes) => void;
  onClose?: () => void;
  modalContext?: FunnelContextArea;
  setFunnelPending?: (pending: boolean) => void;
}) {
  // Check both auth and content funnels to see if any modal is open
  const { pageOrder } = useParams();
  const { releaseData } = useReleaseData();
  const { userData } = useUserData();
  const accountData = useAccountData();

  const [authResults, setAuthResults] = useState<FunnelOnSuccessResult>();
  const setAuthResultsWrapper = (result?: FunnelOnSuccessResult) => {
    setFunnelPending?.(!!result);
    setAuthResults(result);
  };

  const paymentType = usePaywallType(releaseData as Release);
  const navigationContext = useGetNavigationContext({
    contextArea: EventContextArea.reader,
    release: releaseData as Release,
  });

  const isVertical = modalContext === FunnelContextArea.verticalReader;

  const handleGainAccess = useCallback(async () => {
    setAuthResultsWrapper();
    setFunnelConfig();
  }, []);

  const context = useMemo(() => {
    return {
      modalContext,
      purchaseRelease: releaseData as Release,
      navigationContext,
    };
  }, [releaseData, navigationContext]);

  const options = useMemo(() => {
    return {
      locked: true,
      onClose,
    };
  }, [onClose]);

  const releaseHasGoldDiscount = (release?: Release) => {
    const { ppb_gold_price_point, ppb_gold_sale_price_point } = release ?? {};
    return ppb_gold_price_point || ppb_gold_sale_price_point;
  };

  const handlePostCongratsFlow = useCallback(
    (result: FunnelOnSuccessResult) => {
      setFunnelConfig({
        type: FUNNEL_TYPES.purchaseRelease,
        context: result.context,
        options: {
          ...result.options,
          onSuccess: setAuthResultsWrapper,
        },
      });
    },
    [],
  );

  const handlePostPaymentFlow = useCallback(
    (result: FunnelOnSuccessResult) => {
      const release = result.context?.purchaseRelease;
      let { nextFunnelType } = result;
      const isFreeWithGold =
        !nextFunnelType && result.lastFunnelType === FUNNEL_TYPES.subscription;

      if (
        ((result.lastFunnelType === FUNNEL_TYPES.subscription ||
          result.lastFunnelType === FUNNEL_TYPES.offerPaywall) &&
          releaseHasGoldDiscount(release)) ||
        isFreeWithGold
      ) {
        setFunnelConfig({
          type: FUNNEL_TYPES.congrats,
          context: result.context,
          options: {
            ...result.options,
            onSuccess: isFreeWithGold
              ? () => setAuthResultsWrapper(result)
              : handlePostCongratsFlow,
          },
        });
      } else {
        setAuthResultsWrapper(result);
      }
    },
    [handlePostCongratsFlow],
  );

  const handlePrePaywallFlow = useCallback(
    (result: FunnelOnSuccessResult) => {
      let { nextFunnelType } = result;
      const release = result.context?.purchaseRelease;

      const holidayBundleEnabled =
        accountData?.features["holiday_bundle"] ?? false;

      /// Trigger congrats on reader after purchasing yearly subscription during holiday bundle period
      /// only on free with gold, not gold discounted releases
      if (
        !nextFunnelType &&
        result.lastFunnelType === FUNNEL_TYPES.subscription &&
        holidayBundleEnabled &&
        result.options?.purchasedYearly &&
        !releaseHasGoldDiscount(release)
      ) {
        handlePostPaymentFlow(result);
        return;
      }

      setFunnelConfig({
        type: nextFunnelType,
        context: result.context,
        options: {
          ...result.options,
          onSuccess: handlePostPaymentFlow,
        },
      });
    },
    [handlePostPaymentFlow],
  );

  const handlePostAuthFlow = useCallback(
    (result: FunnelOnSuccessResult) => {
      const { userHasAccess } = doesUserHaveAccess(result.data);

      if (userHasAccess) {
        setAuthResultsWrapper(result);
      } else {
        const user = result.data?.[0]?.data?.payload?.results?.user;
        const release = result.context?.purchaseRelease;
        const canOnlyPurchase = user?.gold && releaseHasGoldDiscount(release);
        const type = canOnlyPurchase
          ? FUNNEL_TYPES.purchaseRelease
          : paymentType;

        setFunnelConfig({
          type,
          context: result.context,
          options: {
            ...result.options,
            onSuccess:
              type === FUNNEL_TYPES.prePaywall
                ? handlePrePaywallFlow
                : handlePostPaymentFlow,
          },
        });
      }
    },
    [paymentType, handlePostPaymentFlow],
  );

  const handleSuccessFlow = (result: FunnelOnSuccessResult) => {
    const { data, lastFunnelType } = result;
    const purchaseRelease = data?.[1]?.data?.payload?.results;
    const { userHasBuyAccess, userHasGoldAccess } = doesUserHaveAccess(data);
    const funnelWasAuth =
      lastFunnelType &&
      [
        FUNNEL_TYPES.signup,
        FUNNEL_TYPES.signupEmailPassword,
        FUNNEL_TYPES.login,
      ].includes(lastFunnelType);

    let type;
    if (userHasBuyAccess) {
      if (!funnelWasAuth) {
        handleGainAccess();
        return;
      }
      type = FUNNEL_TYPES.purchaseRelease;
    } else if (userHasGoldAccess) {
      type = funnelWasAuth ? FUNNEL_TYPES.goldAccess : FUNNEL_TYPES.congrats;
    }

    setFunnelConfig({
      type,
      context: {
        ...result.context,
        purchaseRelease,
      },
      options: {
        ...result.options,
        onSuccess: handleGainAccess,
      },
    });
  };

  const openPaywall = useCallback(() => {
    if (userData) {
      setFunnelConfig({
        type: paymentType,
        context,
        options: {
          ...options,
          onSuccess:
            paymentType === FUNNEL_TYPES.prePaywall
              ? handlePrePaywallFlow
              : handlePostPaymentFlow,
        },
      });
    } else {
      setFunnelConfig({
        type: FUNNEL_TYPES.signup,
        context,
        options: {
          ...options,
          skipOnboarding: true,
          onSuccess: handlePostAuthFlow,
        },
      });
    }
  }, [
    userData,
    paymentType,
    context,
    options,
    handlePostPaymentFlow,
    handlePostAuthFlow,
  ]);

  useEffect(() => {
    if (isVertical) return;
    handleGainAccess();
  }, [pageOrder]);

  useEffect(() => {
    if (!openType && showPaywall) openPaywall();
  }, [openType]);

  useEffect(() => {
    if (showPaywall) {
      openPaywall();
    } else if (!showPaywall && !authResults) {
      handleGainAccess();
    }
  }, [showPaywall, authResults]);

  useEffect(() => {
    if (!authResults || showPaywall) return;
    handleSuccessFlow(authResults);
  }, [authResults, showPaywall]);

  // Cleanup on unmount
  useEffect(() => {
    return () => setAuthResultsWrapper();
  }, []);
}
